/*
  Component: Promotion countdown
  Responsibility: count down to the moment a campaign ends, and take the line away once it
                  has passed.

  The target comes from the metaobject's promotion_count_down_date, which is a Single line
  text field rather than a date one — nothing in the admin validates it. So the parse is
  guarded and anything unreadable removes the line rather than rendering NaN at a visitor.
  The markup carries the raw value as its text until this runs, which is why a browser that
  never runs it shows a date instead of an empty row.

  setInterval here is the thing being measured, not a wait for the DOM — the seconds have to
  keep moving. It is cleared the moment the campaign ends, so nothing ticks forever.

  Prefixed constants: every authored script shares one global scope, so a bare SECOND here
  would collide with the same name in another kl-*.js and kill whichever loads second.
*/

const KL_PROMOTION_COUNTDOWN_SECOND = 1000;
const KL_PROMOTION_COUNTDOWN_STORAGE_PREFIX = 'kl:promotion-countdown:';
const KL_PROMOTION_COUNTDOWN_MINUTE = KL_PROMOTION_COUNTDOWN_SECOND * 60;
const KL_PROMOTION_COUNTDOWN_HOUR = KL_PROMOTION_COUNTDOWN_MINUTE * 60;
const KL_PROMOTION_COUNTDOWN_DAY = KL_PROMOTION_COUNTDOWN_HOUR * 24;

/*
  Pure: milliseconds in, the four parts out. DOM-free so the arithmetic can be read and
  checked without a browser — which matters more here than usual, because an off-by-one in a
  remainder is invisible until a campaign is a day from ending.
*/
function getCountdownParts(remainingMs) {
  return {
    days: Math.floor(remainingMs / KL_PROMOTION_COUNTDOWN_DAY),
    hours: Math.floor((remainingMs % KL_PROMOTION_COUNTDOWN_DAY) / KL_PROMOTION_COUNTDOWN_HOUR),
    minutes: Math.floor((remainingMs % KL_PROMOTION_COUNTDOWN_HOUR) / KL_PROMOTION_COUNTDOWN_MINUTE),
    seconds: Math.floor((remainingMs % KL_PROMOTION_COUNTDOWN_MINUTE) / KL_PROMOTION_COUNTDOWN_SECOND),
  };
}

/* Pure. Keep values and units separate so they can use different font weights. */
function formatCountdown(parts) {
  return `<span class="kl-promotion-countdown__value">${parts.days}</span><span class="kl-promotion-countdown__unit">d</span> : <span class="kl-promotion-countdown__value">${parts.hours}</span><span class="kl-promotion-countdown__unit">h</span> : <span class="kl-promotion-countdown__value">${parts.minutes}</span><span class="kl-promotion-countdown__unit">m</span> : <span class="kl-promotion-countdown__value">${parts.seconds}</span><span class="kl-promotion-countdown__unit">s</span>`;
}

/*
  Pure: everything the new-customer window needs to be decided, with the storage read already
  done and the clock passed in, so the three cases can be checked without a browser.

    nothing stored  the visitor is seeing this campaign for the first time — open the window
                    now, and say so, because the caller has to write the deadline down. An
                    hour that restarted on every page view would never run out, and a shopper
                    who reloads once notices.
    still running   use what was stored. A window is per campaign and per browser; clearing
                    storage or moving to another device opens a fresh one, and that is the
                    price of there being nowhere else to keep it.
    already spent   fall back to the campaign's own end date. The offer has not gone when the
                    window closes — only the shorter deadline has — so the honest line is the
                    real one, not no line at all.

  The window never outlives the campaign: a clamp, not a nicety. An hour opened five minutes
  before a sale ends would otherwise promise fifty-five minutes that do not exist.
*/
function getNewCustomerDeadline(storedValue, nowMs, rawMinutes, promotionEndTime) {
  const minutes = Number.parseFloat(rawMinutes);

  if (!Number.isFinite(minutes) || minutes <= 0) {
    return { deadline: promotionEndTime, isStoreNeeded: false };
  }

  const storedDeadline = Number.parseInt(storedValue, 10);

  if (Number.isFinite(storedDeadline)) {
    const isWindowOpen = storedDeadline > nowMs;

    return {
      deadline: isWindowOpen ? Math.min(storedDeadline, promotionEndTime) : promotionEndTime,
      isStoreNeeded: false,
    };
  }

  const openedDeadline = nowMs + minutes * KL_PROMOTION_COUNTDOWN_MINUTE;

  return { deadline: Math.min(openedDeadline, promotionEndTime), isStoreNeeded: true };
}

/*
  localStorage throws outright in some privacy modes rather than returning null, and a sale
  countdown is not worth a broken product page. Both sides fail quiet and loud: the visitor
  gets the campaign deadline, the console gets the reason.
*/
function readStoredDeadline(storageKey) {
  try {
    return localStorage.getItem(storageKey);
  } catch (error) {
    console.debug('kl-promotion-countdown: stored deadline could not be read', { storageKey, error });
    return null;
  }
}

function writeStoredDeadline(storageKey, deadline) {
  try {
    localStorage.setItem(storageKey, String(deadline));
  } catch (error) {
    console.debug('kl-promotion-countdown: deadline could not be saved', { storageKey, error });
  }
}

function initPromotionCountdown(countdownElement) {
  const timeElement = countdownElement.querySelector('[data-kl-promotion-countdown-time]');
  const labelElement = countdownElement.querySelector('[data-kl-promotion-countdown-label]');
  const { klPromotionCountdownTarget: targetValue, klPromotionCountdownKey: promotionKey } =
    countdownElement.dataset;

  if (!timeElement || !targetValue) return;

  const targetTime = Date.parse(targetValue);

  if (Number.isNaN(targetTime)) {
    countdownElement.hidden = true;
    console.debug('kl-promotion-countdown: unreadable end date', { targetValue });
    return;
  }

  /*
    Which visitor this is cannot be settled on the server for a logged-out shopper, so the
    attribute carries what Liquid could work out and assets/kl-visitor-audience.js finishes
    the job. That file loads first — see layout/theme.liquid — and a missing one leaves every
    visitor on the campaign deadline, which is the pre-existing behaviour rather than a break.
  */
  const isNewCustomer =
    typeof getVisitorAudience === 'function' &&
    getVisitorAudience(countdownElement.dataset.klPromotionCountdownAudience) === 'new';

  let deadlineTime = targetTime;

  if (isNewCustomer && promotionKey) {
    const storageKey = `${KL_PROMOTION_COUNTDOWN_STORAGE_PREFIX}${promotionKey}`;
    const { deadline, isStoreNeeded } = getNewCustomerDeadline(
      readStoredDeadline(storageKey),
      Date.now(),
      countdownElement.dataset.klPromotionCountdownMinutes,
      targetTime
    );

    if (isStoreNeeded) writeStoredDeadline(storageKey, deadline);

    deadlineTime = deadline;
  }

  /*
    The campaign's own wording is what Liquid rendered, so it is read off the element rather
    than passed twice. The swap happens only while the shorter deadline is actually in force:
    a new visitor whose window has already closed is reading the campaign deadline and must
    read the campaign's words with it.
  */
  const fullPromotionLabel = labelElement ? labelElement.textContent.trim() : '';
  const newCustomerLabel = countdownElement.dataset.klPromotionCountdownLabelNew;

  if (labelElement && isNewCustomer && newCustomerLabel && deadlineTime < targetTime) {
    labelElement.textContent = newCustomerLabel;
  }

  let intervalId = null;

  function render() {
    const remainingMs = deadlineTime - Date.now();

    /*
      The short window closing mid-visit is not the campaign ending: hand the line back to the
      campaign deadline and its own wording, and keep ticking. Only the second expiry below
      takes the line away.
    */
    if (remainingMs <= 0 && deadlineTime < targetTime) {
      deadlineTime = targetTime;

      if (labelElement && fullPromotionLabel) labelElement.textContent = fullPromotionLabel;

      render();

      return;
    }

    /*
      Past its end the campaign is over, and a line saying so is worse than no line: the
      chip and the price beside it are still showing the offer, so the honest thing is to
      stop claiming a deadline that has gone.
    */
    if (remainingMs <= 0) {
      countdownElement.hidden = true;

      if (intervalId) clearInterval(intervalId);

      return;
    }

    timeElement.innerHTML = formatCountdown(getCountdownParts(remainingMs));

    /*
      Shown only once it has something true to say. The markup renders hidden precisely so
      that nobody reads a placeholder while this script is on its way — see the note in
      snippets/kl-promotion.liquid. Setting it every tick rather than once is free and means
      the line comes back by itself if anything else ever hides it.
    */
    countdownElement.hidden = false;
  }

  render();

  if (!countdownElement.hidden) {
    intervalId = setInterval(render, KL_PROMOTION_COUNTDOWN_SECOND);
  }
}

document.querySelectorAll('[data-kl-promotion-countdown]').forEach(initPromotionCountdown);

// Theme editor: a re-rendered section arrives with fresh, uninitialised markup.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-kl-promotion-countdown]').forEach(initPromotionCountdown);
});
